"use client"

import Link from "next/link"
import { IconArrowRight, IconChevronRight, IconInboxOff } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { usePermissions } from "@/hooks/use-permissions"

import type { Lead, PaginatedResponse } from "@/types"

/** Rows rendered inside the panel; the rest lives behind the footer link. */
export const UNASSIGNED_QUEUE_LIMIT = 5

/** Pre-filtered leads index — the queue's "see everything" destination. */
const UNASSIGNED_LEADS_HREF = "/leads?unassigned=true"

interface UnassignedLeadsQueueProps {
  /**
   * The full paginated envelope from {@link import('@/lib/leads-api').getUnassignedLeads} —
   * `data` feeds the rows and `meta.total` feeds the footer count, which is why
   * the envelope (not just the array) is passed down.
   */
  data: PaginatedResponse<Lead> | null
  isLoading: boolean
  /** Extra classes merged onto the panel. */
  className?: string
}

/**
 * "Needs an owner" queue — the team leader's action item: the newest leads that
 * nobody is working yet, with a per-row jump to the lead so a decision can be
 * made without leaving the dashboard.
 *
 * PRESENTATIONAL ONLY — the caller owns the fetch and the `leads.view.all`
 * gate (a user scoped to `leads.view.assigned` can never see an unassigned
 * lead, so the widget is omitted rather than rendered empty). The one gate the
 * widget owns is the per-row assign affordance, which requires `leads.assign`;
 * `usePermissions().can()` stays false until the grant set has resolved, so the
 * link never flashes for a user who is not allowed to act on the row.
 */
export function UnassignedLeadsQueue({
  data,
  isLoading,
  className,
}: UnassignedLeadsQueueProps) {
  const t = useTranslations("dashboard")
  const formatter = useFormatter()
  const { can } = usePermissions()

  const canAssign = can("leads.assign")

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("unassignedQueue.title")} className={className}>
        <div className="space-y-3">
          {[...Array(UNASSIGNED_QUEUE_LIMIT)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/3" />
              </div>
              <Skeleton className="h-5 w-16 shrink-0 rounded-full" />
            </div>
          ))}
        </div>
      </ApolloPanel>
    )
  }

  const rows = data?.data ?? []
  const total = data?.meta?.total ?? rows.length

  if (rows.length === 0) {
    return (
      <ApolloPanel eyebrow={t("unassignedQueue.title")} className={className}>
        <div className="flex h-[200px] flex-col items-center justify-center text-center">
          <div className="mb-3 rounded-full bg-muted p-3">
            <IconInboxOff className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("unassignedQueue.empty.title")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            {t("unassignedQueue.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  /** Localized "3 hours ago"; null for a stamp we cannot parse. */
  const relativeCreatedAt = (iso: string | null | undefined): string | null => {
    if (!iso) return null
    const date = new Date(iso)
    return Number.isNaN(date.getTime()) ? null : formatter.relativeTime(date)
  }

  return (
    <ApolloPanel
      eyebrow={t("unassignedQueue.title")}
      actions={
        <Badge variant="outline" className="text-[10px]">
          {t("unassignedQueue.count", { count: total })}
        </Badge>
      }
      className={className}
    >
      <div className="space-y-1">
        {rows.map((lead) => {
          const relative = relativeCreatedAt(lead.created_at)

          return (
            <div
              key={lead.id}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/60"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{lead.full_name}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-text-dim">
                  <span className="truncate">
                    {lead.source?.name ?? t("unassignedQueue.noSource")}
                  </span>
                  {relative && (
                    <>
                      <span aria-hidden>·</span>
                      <span className="shrink-0">{relative}</span>
                    </>
                  )}
                </div>
              </div>

              {lead.status && (
                <Badge variant="outline" className="shrink-0 text-[10px]">
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: lead.status.color }}
                    aria-hidden
                  />
                  {lead.status.name}
                </Badge>
              )}

              {canAssign && (
                <Link
                  href={`/leads/${lead.id}`}
                  className="flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none"
                >
                  {t("unassignedQueue.assign")}
                  <IconChevronRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>
          )
        })}
      </div>

      <div className="mt-3 border-t border-border pt-3">
        <Link
          href={UNASSIGNED_LEADS_HREF}
          className="group inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {t("unassignedQueue.viewAll", { count: total })}
          <IconArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>
    </ApolloPanel>
  )
}
